import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/StatusBits";
import { useApp } from "@/context/AppContext";
import { api, errMsg, label } from "@/lib/api";

const STATUSES = ["new", "attempting", "callback_scheduled", "contacted", "resolved", "do_not_call", "opted_out", "wrong_number"];
const EMPTY = { name: "", phone: "", business: "", email: "", notes: "", tags: [], assigned_agent_id: null, timezone: "", status: "new", callback_at: "",
  amount_owed: "", approval_amount: "", lender: "", terms: "", missing_documents: "", offer_details: "", sms_consent: false };

const toLocal = (v) => (v ? new Date(new Date(v).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "");

export const ContactDialog = ({ open, onOpenChange, contact, onSaved }) => {
  const { isAdmin } = useApp();
  const [f, setF] = useState(EMPTY);
  const [agents, setAgents] = useState([]);
  useEffect(() => {
    if (open) setF(contact ? { ...EMPTY, ...contact, notes: "", callback_at: toLocal(contact.callback_at) } : EMPTY);
  }, [open, contact]);
  useEffect(() => {
    if (open && isAdmin) api.get("/users").then((r) => setAgents(r.data.users.filter((u) => u.role !== "pending"))).catch(() => {});
  }, [open, isAdmin]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    const body = { ...f, tags: typeof f.tags === "string" ? f.tags.split(",") : f.tags, callback_at: f.callback_at ? new Date(f.callback_at).toISOString() : null };
    try {
      if (contact) await api.put(`/contacts/${contact.id}`, body);
      else await api.post("/contacts", body);
      toast.success("Contact saved");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const txt = (k, l) => <Field label={l}><Input data-testid={`contact-form-${k}`} value={f[k] || ""} onChange={set(k)} /></Field>;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-dialog">
        <DialogHeader><DialogTitle>{contact ? "Edit contact" : "New contact"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {txt("name", "Name *")}{txt("phone", "Phone *")}{txt("business", "Business")}{txt("email", "Email")}
          <Field label="Tags (comma separated)"><Input data-testid="contact-form-tags" value={Array.isArray(f.tags) ? f.tags.join(", ") : f.tags} onChange={set("tags")} /></Field>
          {txt("timezone", "Timezone (e.g. America/Chicago)")}
          <Field label="Call status">
            <Select value={f.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="contact-form-status" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Callback time"><Input type="datetime-local" data-testid="contact-form-callback" value={f.callback_at || ""} onChange={set("callback_at")} /></Field>
          {isAdmin && (
            <Field label="Assigned agent">
              <Select value={f.assigned_agent_id || "none"} onValueChange={(v) => setF({ ...f, assigned_agent_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="contact-form-agent" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">Unassigned</SelectItem>
                  {agents.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name || a.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <label className="flex items-center gap-2 text-sm pt-5"><Switch data-testid="contact-form-sms-consent" checked={!!f.sms_consent} onCheckedChange={set("sms_consent")} /> Has given SMS permission</label>
        </div>
        <p className="text-xs font-semibold text-slate-700 pt-2">Account information for the AI (optional)</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {txt("amount_owed", "Amount owed")}{txt("approval_amount", "Approval amount")}{txt("lender", "Lender")}
          {txt("terms", "Terms")}{txt("missing_documents", "Missing documents")}{txt("offer_details", "Offer details")}
        </div>
        {!contact && <Field label="Notes"><Textarea data-testid="contact-form-notes" rows={2} value={f.notes} onChange={set("notes")} /></Field>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contact-form-cancel">Cancel</Button>
          <Button onClick={save} data-testid="contact-form-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
